import './index.css';

import { StrictMode } from 'react';
import type { JSX } from 'react';
import { createRoot } from 'react-dom/client';
import { addToast, Button, closeToast } from '@heroui/react';
import * as Sentry from '@sentry/react';
import * as reactQuery from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import App from './App';
import { getApiConfig, getConfig, getMonitoringConfig } from './config';
import { ApiError, markApiDown, markApiUp } from './services/api/client';
import { monitoringService } from './services/monitoring';
import { logger } from './utils/logger';
import { getExponentialBackoff, RETRY_STRATEGIES } from './utils/retry';

logger.info({ component: 'main' }, 'Starting application initialization');

// Module-level flag to prevent double initialization during HMR/StrictMode
let monitoringInitialized = false;

// Initialize monitoring service
function initializeMonitoring(): boolean {
  logger.info({ component: 'main' }, 'Initializing monitoring service');

  // Log current configuration
  const config = getConfig();
  logger.info({ component: 'main' }, 'App Configuration', {
    environment: config.monitoring.environment,
    apiEndpoints: config.api.endpoints,
  });

  // Initialize monitoring service with new config
  const monitoringConfig = getMonitoringConfig();
  if (!monitoringConfig.dsn) {
    logger.warn(
      { component: 'main' },
      'Sentry DSN is not configured. Error monitoring will be disabled.',
    );
    logger.info(
      { component: 'main' },
      'This is normal for local development. To enable Sentry, set VITE_SENTRY_DSN in your .env file',
    );
    return false;
  }

  const success = monitoringService.init(monitoringConfig);

  if (!success) {
    const error = monitoringService.getInitializationError();
    logger.error(
      { component: 'main' },
      'Failed to initialize monitoring service',
      error instanceof Error ? error : undefined,
      {
        error: error instanceof Error ? error.message : String(error),
      },
    );
  } else {
    logger.info({ component: 'main' }, 'Monitoring service initialized successfully');
  }

  return success;
}

// Initialize monitoring before setting up React
// Use module-level flag to prevent double initialization during HMR/StrictMode
if (!monitoringInitialized) {
  try {
    logger.info({ component: 'main' }, 'Starting monitoring initialization');
    const success = initializeMonitoring();
    monitoringInitialized = true; // Set flag after first attempt
    logger.info({ component: 'main' }, 'Monitoring initialization completed', {
      status: success,
    });
  } catch (error) {
    logger.error(
      { component: 'main' },
      'Failed to initialize monitoring',
      error instanceof Error ? error : undefined,
      {
        error: error instanceof Error ? error.message : String(error),
      },
    );
    monitoringInitialized = true; // Set flag even on error to prevent retries
  }
} else {
  logger.debug(
    { component: 'main' },
    'Monitoring already initialized, skipping duplicate initialization',
  );
}

// Track consecutive failures and API availability
let consecutiveFailures = 0;
// Toast key returned by addToast so we can dismiss the lost-connection toast on recovery
// (addToast with timeout: 0 never auto-dismisses; null means the queue rejected the add).
let connectionLostToastId: string | null = null;
let apiMarkedOffline = false;

function isConnectivityFailure(error: unknown): boolean {
  if (error instanceof ApiError) {
    // Only treat unknown-status transport failures as connectivity outages.
    return typeof error.status !== 'number';
  }

  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes('network') || message.includes('fetch') || message.includes('connection')
    );
  }

  return false;
}

// Global error handler for queries - shows toast on failures and pauses queries when backend is down
const queryCache = new reactQuery.QueryCache({
  onError: (error, query): void => {
    if (!isConnectivityFailure(error)) {
      logger.debug({ component: 'QueryClient' }, 'Ignoring non-connectivity query failure', {
        queryKey: query.queryKey,
        error: error instanceof Error ? error.message : String(error),
      });
      return;
    }

    consecutiveFailures++;

    // Log individual errors for debugging
    logger.debug({ component: 'QueryClient' }, 'Query failed', {
      queryKey: query.queryKey,
      error: error instanceof Error ? error.message : String(error),
      consecutiveFailures,
    });

    // After 3 consecutive failures, mark as "offline" to STOP all queries
    if (consecutiveFailures >= 3 && !apiMarkedOffline) {
      apiMarkedOffline = true;
      // Tell API client to fail fast (no retries)
      markApiDown();
      // Tell React Query we're "offline" so it pauses ALL queries
      reactQuery.onlineManager.setOnline(false);

      if (connectionLostToastId === null) {
        connectionLostToastId = addToast({
          description: 'Connection lost. Will retry when available.',
          color: 'warning',
          timeout: 0, // Persistent — dismissed explicitly on recovery (see closeToast below)
        });
      }

      logger.warn({ component: 'QueryClient' }, 'API marked offline - queries paused', {
        consecutiveFailures,
        queryKey: query.queryKey,
      });

      // Start a recovery check every 10 seconds
      startRecoveryCheck();
    }
  },
  onSuccess: (): void => {
    // Reset failure counter and re-enable queries on any success
    if (consecutiveFailures > 0 || apiMarkedOffline) {
      logger.info({ component: 'QueryClient' }, 'Query succeeded - resetting failure state', {
        consecutiveFailures,
        apiMarkedOffline,
      });

      consecutiveFailures = 0;

      if (apiMarkedOffline) {
        apiMarkedOffline = false;
        // Re-enable queries
        markApiUp(); // Tell API client to resume normal operation
        reactQuery.onlineManager.setOnline(true);
        stopRecoveryCheck();

        if (connectionLostToastId !== null) {
          closeToast(connectionLostToastId);
          connectionLostToastId = null;
          addToast({
            description: 'Connection restored',
            color: 'success',
            timeout: 3000,
          });
        }

        // onlineManager.setOnline(true) only resumes PAUSED queries. Queries that
        // already failed-and-finished during the outage stay in their error state —
        // invalidate them so they refetch now that the API is reachable.
        void queryClient.invalidateQueries();

        logger.info({ component: 'QueryClient' }, 'API marked online - queries resumed');
      }
    }
  },
});

// Check if error is a silent/expected error that shouldn't show a toast
function isSilentMutationError(error: unknown): boolean {
  // Check for 409 "agent is currently processing" errors
  // These are expected when users rapidly click buttons while the agent is busy
  const apiError = error as { message?: string; status?: number };
  if (apiError.status === 409) {
    const message = (error instanceof Error ? error.message : '').toLowerCase();
    if (
      message.includes('currently processing') ||
      message.includes('already processing') ||
      message.includes('agent is')
    ) {
      return true;
    }
  }
  return false;
}

// Recovery check - periodically try to reach the API when marked offline
let recoveryInterval: ReturnType<typeof setInterval> | null = null;

function startRecoveryCheck(): void {
  if (recoveryInterval) return; // Already running

  logger.info({ component: 'QueryClient' }, 'Starting recovery check interval');

  recoveryInterval = setInterval(() => {
    // Try to fetch ping endpoint directly (not through React Query).
    // Must use the configured API baseURL — a relative '/api/ping' would hit
    // the web origin (different host in dev/E2E/prod), giving SPA-fallback 200s
    // on HTML and producing false-positive recoveries.
    fetch(`${getApiConfig().baseURL}/ping`, { method: 'GET' })
      .then(response => {
        if (response.ok) {
          logger.info({ component: 'QueryClient' }, 'Recovery check succeeded');
          // Reset state and re-enable queries
          consecutiveFailures = 0;
          apiMarkedOffline = false;
          markApiUp(); // Tell API client to resume normal operation
          reactQuery.onlineManager.setOnline(true);
          stopRecoveryCheck();

          if (connectionLostToastId !== null) {
            closeToast(connectionLostToastId);
            connectionLostToastId = null;
            addToast({
              description: 'Connection restored',
              color: 'success',
              timeout: 3000,
            });
          }

          // onlineManager.setOnline(true) only resumes PAUSED queries. Queries that
          // already failed-and-finished during the outage stay in their error state —
          // invalidate them so they refetch now that the API is reachable.
          void queryClient.invalidateQueries();
        }
      })
      .catch(() => {
        logger.debug({ component: 'QueryClient' }, 'Recovery check failed - still offline');
      });
  }, 10000); // Check every 10 seconds
}

function stopRecoveryCheck(): void {
  if (recoveryInterval) {
    clearInterval(recoveryInterval);
    recoveryInterval = null;
    logger.info({ component: 'QueryClient' }, 'Stopped recovery check interval');
  }
}

// Global error handler for mutations - shows toast on mutation failures
const mutationCache = new reactQuery.MutationCache({
  onError: (error, _variables, _context, mutation): void => {
    // Skip toast for expected/silent errors (like rapid-click 409s)
    if (isSilentMutationError(error)) {
      logger.debug({ component: 'QueryClient' }, 'Silent mutation error (no toast)', {
        mutationKey: mutation.options.mutationKey,
        error: error instanceof Error ? error.message : String(error),
      });
      return;
    }

    // Show toast for mutation errors (these are user actions, so always notify)
    const errorMessage = error instanceof Error ? error.message : 'An error occurred';
    addToast({
      description: `Failed to save: ${errorMessage}`,
      color: 'danger',
      timeout: 5000,
    });
    logger.error(
      { component: 'QueryClient' },
      'Mutation failed',
      error instanceof Error ? error : undefined,
      {
        mutationKey: mutation.options.mutationKey,
      },
    );
  },
});

const queryClient = new reactQuery.QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // Data stays fresh for 5 minutes - prevents spam refetches
      gcTime: 1000 * 60 * 60, // Unused data is garbage collected after 1 hour
      retry: 1, // Only retry once - reduces spam when backend is down
      retryDelay: (attemptIndex): number =>
        getExponentialBackoff(
          attemptIndex,
          RETRY_STRATEGIES.REST_API.baseDelay,
          RETRY_STRATEGIES.REST_API.maxDelay,
        ),
      refetchOnWindowFocus: false, // Don't auto-refetch - user can manually refresh
      refetchOnReconnect: false, // Don't refetch when reconnecting - prevents spam when flaky
      refetchOnMount: false, // Don't refetch on component mount
      // CRITICAL: Pause queries when offline - don't spam failed requests
      networkMode: 'online',
      // CRITICAL: Don't throw errors to components - let them show stale data
      throwOnError: false,
    },
    mutations: {
      retry: 1, // Only retry mutations once (user actions need quick feedback)
      retryDelay: 2000, // Wait 2s before retry
      // CRITICAL: Pause mutations when offline - queue them for when back online
      networkMode: 'online',
    },
  },
});

const rootElement = document.getElementById('root');
logger.debug({ component: 'main' }, 'Root element found', {
  rootElement: !!rootElement,
});

if (!rootElement) {
  throw new Error('Failed to find root element');
}

// Create error boundary fallback
export function ErrorFallback({
  componentStack,
  error,
  eventId,
  resetError,
}: {
  componentStack: string;
  error: unknown;
  eventId: string;
  resetError: () => void;
}): JSX.Element {
  return (
    <div className="flex h-screen items-center justify-center font-sans p-4">
      <div className="max-w-lg text-center p-8 bg-surface rounded-lg shadow">
        <h1 className="text-2xl font-bold text-error mb-4">Something went wrong</h1>
        <p className="text-foreground dark:text-muted mb-4">
          The application encountered an error. Our team has been notified.
          {eventId && <span className="block mt-2 text-sm">Event ID: {eventId}</span>}
        </p>
        <pre className="bg-surface-elevated dark:bg-background p-4 rounded text-sm text-left overflow-x-auto mb-4">
          {error instanceof Error ? error.message : String(error)}
        </pre>
        {componentStack && (
          <pre className="bg-surface-elevated dark:bg-background p-4 rounded text-sm text-left overflow-x-auto mb-4">
            {componentStack}
          </pre>
        )}
        <div className="mt-4 space-x-4">
          <Button color="secondary" onPress={resetError}>
            Try Again
          </Button>
          <Button color="secondary" variant="bordered" onPress={() => window.location.reload()}>
            Refresh Page
          </Button>
        </div>
      </div>
    </div>
  );
}

try {
  logger.info({ component: 'main' }, 'Creating React root');
  const root = createRoot(rootElement);

  logger.info({ component: 'main' }, 'Rendering application');
  root.render(
    <StrictMode>
      <Sentry.ErrorBoundary fallback={ErrorFallback}>
        <reactQuery.QueryClientProvider client={queryClient}>
          <App />
          <ReactQueryDevtools initialIsOpen={false} />
        </reactQuery.QueryClientProvider>
      </Sentry.ErrorBoundary>
    </StrictMode>,
  );
  logger.info({ component: 'main' }, 'Application rendered successfully');
} catch (error) {
  logger.error(
    { component: 'main' },
    'Error rendering application',
    error instanceof Error ? error : undefined,
    {
      error: error instanceof Error ? error.message : String(error),
    },
  );
  // Show a fallback UI
  if (rootElement) {
    rootElement.innerHTML = `
      <div class="flex h-screen items-center justify-center font-sans p-4">
        <div class="max-w-lg text-center p-8 bg-surface rounded-lg shadow">
          <h1 class="text-2xl font-bold text-error dark:text-error mb-4">Something went wrong</h1>
          <p class="text-secondary-600 dark:text-muted mb-4">
            The application failed to load. Please try refreshing the page.
          </p>
          <pre class="bg-surface-elevated dark:bg-background p-4 rounded text-sm text-left overflow-x-auto">
            ${error instanceof Error ? error.message : 'Unknown error'}
          </pre>
          <button
            onclick="window.location.reload()"
            class="mt-4 px-4 py-2 bg-primary dark:bg-primary/90 text-white rounded hover:bg-primary/80 dark:hover:bg-primary transition-colors"
          >
            Refresh Page
          </button>
        </div>
      </div>
    `;
  }
}
